import {
  Box,
  EmptyView,
  LoaderTalos,
  LoaderWrapper,
  Stack,
  type Customer,
  type CustomerNetAcquisitionLimitStatus,
  type MarketAccount,
} from '@talos/kyoko';
import type { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { useAcquisitionLimitStatusQuery } from '../queries';
import { CustomerSelector } from './CustomerSelector';
import { LimitStatus } from './LimitStatus';

interface DialogDataProps {
  marketAccount: MarketAccount | undefined;
  limitQuery: UseQueryResult<CustomerNetAcquisitionLimitStatus, Error>;
}

function DialogData({ marketAccount, limitQuery }: DialogDataProps) {
  if (!marketAccount) {
    return null;
  } else if (limitQuery.isLoading) {
    return (
      <LoaderWrapper flex={1}>
        <LoaderTalos />
      </LoaderWrapper>
    );
  } else if (limitQuery.error || !limitQuery.data) {
    return (
      <Box flex={1}>
        <EmptyView>{limitQuery.error?.message ?? 'Limit status information not available'}</EmptyView>
      </Box>
    );
  } else {
    return <LimitStatus statusData={limitQuery.data} />;
  }
}

interface DialogContentsProps {
  customers: Customer[];
  customerMarketAccountsList: MarketAccount[];
  preselectedMarketAccount: MarketAccount | undefined;
}

export function DialogContents({
  customers,
  customerMarketAccountsList,
  preselectedMarketAccount,
}: DialogContentsProps) {
  const [marketAccount, setMarketAccount] = useState<MarketAccount | undefined>(preselectedMarketAccount);
  const limitQuery = useAcquisitionLimitStatusQuery(marketAccount);

  return (
    <Stack minHeight={200}>
      <CustomerSelector
        customers={customers}
        customerMarketAccountsList={customerMarketAccountsList}
        marketAccount={marketAccount}
        onChange={setMarketAccount}
      />
      <DialogData marketAccount={marketAccount} limitQuery={limitQuery} />
    </Stack>
  );
}
